import { type Board, defaultBoard, type HomeBoardDto } from "@kourai/api/home-board";
import { useCallback, useEffect, useRef, useState } from "react";
import { useApi } from "@/lib/api-client";
import {
  type CachedBoard,
  cacheKey,
  parseCachedBoard,
  readCachedBoard,
  writeCachedBoard,
} from "@/lib/home/board-cache";
import { fromStarter, sameLayout } from "@/lib/home/layout";
import { rebase } from "@/lib/home/sync";
import { RESTORED_EVENT } from "./use-widget-data";

/** Edits save this long after the last change. */
const SAVE_DELAY_MS = 800;

type State = CachedBoard & { loaded: boolean };

const fresh = (dto: HomeBoardDto): State => ({
  board: dto.board,
  base: dto.board,
  version: dto.version,
  source: dto.source,
  dirty: false,
  loaded: true,
});

/**
 * Your board in the active workspace (ADMIN-258, ADMIN-259).
 *
 * - **Instant**: paints from the local copy (localStorage) before any request, then refreshes
 *   from the server.
 * - **Saves** about a second after the last edit, with the version it was based on (If-Match).
 * - **Other tabs** update at once: they all read the same local copy, and the `storage` event
 *   tells them it changed.
 * - **Other devices** are caught up when the window gets focus or the workspace changes.
 * - **Conflicts**: if another device saved in between, this device's edits are replayed on top of
 *   its board widget by widget (`rebase`) and saved again, so nothing is silently lost.
 */
export function useHomeBoard(userId: string, workspaceId: string | null) {
  const api = useApi();
  const initial = (): State => {
    const cached = workspaceId ? readCachedBoard(userId, workspaceId) : null;
    return cached
      ? { ...cached, loaded: false }
      : { board: defaultBoard(), version: 0, source: "default", loaded: false };
  };
  const [state, setState] = useState<State>(initial);
  /** The workspace's starter, as the last GET described it (ADMIN-264). */
  const [starter, setStarter] = useState<Pick<HomeBoardDto, "starterVersion" | "starterOffer">>({});
  const stateRef = useRef(state);
  stateRef.current = state;
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saving = useRef(false);
  /** The workspace on screen, so a late answer for the previous one is dropped. */
  const shown = useRef(workspaceId);
  shown.current = workspaceId;
  const saveRef = useRef<() => Promise<void>>(async () => {});
  const schedule = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      timer.current = null;
      void saveRef.current();
    }, SAVE_DELAY_MS);
  }, []);

  const remember = useCallback(
    (next: State) => {
      stateRef.current = next;
      setState(next);
      if (workspaceId) {
        const { loaded: _loaded, ...cached } = next;
        writeCachedBoard(userId, workspaceId, cached);
      }
    },
    [userId, workspaceId],
  );

  /**
   * A board from the server (a GET, or a 409's current board). Clean: take it. With unsaved
   * edits: replay them on top of it and save again.
   */
  const adopt = useCallback(
    (dto: HomeBoardDto) => {
      const current = stateRef.current;
      if (!current.dirty) {
        if (dto.version !== current.version || !sameLayout(dto.board, current.board))
          remember(fresh(dto));
        else if (!current.loaded) setState({ ...current, loaded: true });
        return;
      }
      if (dto.version === current.version) {
        // Nobody saved in between: these edits just haven't gone out yet.
        if (!current.loaded) setState({ ...current, loaded: true });
        schedule();
        return;
      }
      const merged = rebase(current.base ?? dto.board, current.board, dto.board);
      remember({
        board: merged,
        base: dto.board,
        version: dto.version,
        source: dto.source,
        dirty: true,
        loaded: true,
      });
      schedule();
    },
    [remember, schedule],
  );

  const refresh = useCallback(async () => {
    if (!workspaceId) return;
    try {
      const res = await api.api.workspaces[":id"].home.$get({ param: { id: workspaceId } });
      const dto = res.ok ? ((await res.json()) as HomeBoardDto) : null;
      if (dto && shown.current === workspaceId) {
        adopt(dto);
        setStarter({ starterVersion: dto.starterVersion, starterOffer: dto.starterOffer });
      }
    } catch {
      setState((s) => (s.loaded ? s : { ...s, loaded: true }));
    }
  }, [api, workspaceId, adopt]);

  // A new workspace (or person): show its local copy right away, then ask the server.
  // biome-ignore lint/correctness/useExhaustiveDependencies: initial reads the new keys
  useEffect(() => {
    const next = initial();
    stateRef.current = next;
    setState(next);
    setStarter({});
    void refresh();
  }, [userId, workspaceId, refresh]);

  // Other devices: catch up when this window comes back.
  useEffect(() => {
    const onFocus = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [refresh]);

  // Other tabs write the same local copy: show what they wrote. A tab with its own edits waiting
  // to go out keeps them; its save conflicts and replays them on top instead.
  useEffect(() => {
    if (!workspaceId) return;
    const key = cacheKey(userId, workspaceId);
    const onStorage = (e: StorageEvent) => {
      if (e.key !== key || timer.current || saving.current) return;
      const next = parseCachedBoard(e.newValue);
      if (!next) return;
      const updated = { ...next, loaded: true };
      stateRef.current = updated;
      setState(updated);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [userId, workspaceId]);

  const save = useCallback(async () => {
    const current = stateRef.current;
    if (!workspaceId || !current.dirty || saving.current) return;
    saving.current = true;
    try {
      const res = await api.api.workspaces[":id"].home.$put(
        { param: { id: workspaceId }, json: { board: current.board } },
        { headers: { "If-Match": `"${current.version}"` } },
      );
      const body = (await res.json()) as {
        version?: number;
        restored?: string[];
        current?: HomeBoardDto | null;
      };
      saving.current = false;
      if (shown.current !== workspaceId) return;
      const latest = stateRef.current;
      if (res.ok && typeof body.version === "number") {
        // Still dirty if more edits came in while this one was in flight.
        const dirty = latest.board !== current.board;
        remember({
          ...latest,
          base: current.board,
          version: body.version,
          source: "member",
          dirty,
        });
        if (dirty) schedule();
        // Widgets added back that took over a removed one's content: let them load it.
        if (body.restored?.length)
          window.dispatchEvent(new CustomEvent(RESTORED_EVENT, { detail: body.restored }));
      } else if (res.status === 409 && body.current) {
        adopt(body.current);
      }
    } catch {
      // Offline: the edit stays in the local copy (dirty) and goes out on the next save or focus.
    } finally {
      saving.current = false;
    }
  }, [api, workspaceId, remember, schedule, adopt]);
  saveRef.current = save;

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  /** Apply an edit. Edits that change nothing aren't saved. */
  const update = useCallback(
    (edit: (board: Board) => Board) => {
      const current = stateRef.current;
      const next = edit(current.board);
      if (next === current.board || sameLayout(next, current.board)) return;
      remember({ ...current, board: next, dirty: true });
      schedule();
    },
    [remember, schedule],
  );

  /** Owners and admins: this board becomes what new members start from. */
  const publishStarter = useCallback(async () => {
    if (!workspaceId) return false;
    const res = await api.api.workspaces[":id"].home.starter
      .$put({ param: { id: workspaceId }, json: { board: stateRef.current.board } })
      .catch(() => null);
    const body = res?.ok ? ((await res.json()) as { version?: number }) : null;
    if (typeof body?.version === "number")
      setStarter((s) => ({ ...s, starterVersion: body.version }));
    return typeof body?.version === "number";
  }, [api, workspaceId]);

  const clearStarter = useCallback(async () => {
    if (!workspaceId) return false;
    const res = await api.api.workspaces[":id"].home.starter
      .$delete({ param: { id: workspaceId } })
      .catch(() => null);
    if (res?.ok) setStarter((s) => ({ ...s, starterVersion: undefined }));
    return !!res?.ok;
  }, [api, workspaceId]);

  /** "Use it or keep your own", answered: not offered again until a new starter is published. */
  const answerStarterOffer = useCallback(
    (use: boolean) => {
      const offer = starter.starterOffer;
      if (!workspaceId || !offer) return;
      if (use) update(() => fromStarter(offer.board));
      setStarter((s) => ({ ...s, starterOffer: undefined }));
      void api.api.workspaces[":id"].home.starter.seen
        .$post({ param: { id: workspaceId }, json: { version: offer.version } })
        .catch(() => {});
    },
    [api, workspaceId, starter.starterOffer, update],
  );

  return {
    board: state.board,
    source: state.source,
    loaded: state.loaded,
    update,
    starterVersion: starter.starterVersion,
    starterOffer: starter.starterOffer,
    publishStarter,
    clearStarter,
    answerStarterOffer,
  };
}
